import React, { useEffect, useMemo, useRef, useState } from 'react';

type SimCalculatorProps = {
  isOpen: boolean;
  onClose: () => void;
  resetToken?: string;
  positionStorageKey?: string;
  onCalculatorEvent?: (eventName: 'calculator_opened' | 'calculator_closed' | 'calculator_used') => void;
};

type Operator = '+' | '-' | '×' | '÷' | null;

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return 'Error';
  const asString = value.toString();
  return asString.length > 14 ? value.toExponential(6) : asString;
};

const compute = (left: number, right: number, operator: Exclude<Operator, null>): number => {
  if (operator === '+') return left + right;
  if (operator === '-') return left - right;
  if (operator === '×') return left * right;
  if (operator === '÷') return right === 0 ? Number.NaN : left / right;
  return right;
};

const SimCalculator: React.FC<SimCalculatorProps> = ({
  isOpen,
  onClose,
  resetToken,
  positionStorageKey = 'sim_calc_pos',
  onCalculatorEvent,
}) => {
  const [display, setDisplay] = useState('0');
  const [storedValue, setStoredValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [desktopPosition, setDesktopPosition] = useState({ x: 0, y: 0 });
  const [positionReady, setPositionReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const firstButtonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragPointerIdRef = useRef<number | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const hasAnnouncedUsedRef = useRef(false);
  const hasAnnouncedOpenRef = useRef(false);
  const prevOpenRef = useRef(false);

  const buttonClass =
    'rounded-input border border-line bg-surface-card px-4 py-3 text-sm font-semibold text-txt-primary transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400';
  const operatorClass =
    'rounded-input border border-line bg-surface-card px-4 py-3 text-sm font-semibold text-emerald-300 transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400';

  const resetCalculator = () => {
    setDisplay('0');
    setStoredValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  useEffect(() => {
    resetCalculator();
  }, [resetToken]);

  useEffect(() => {
    if (!isOpen) return;
    firstButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      hasAnnouncedUsedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (isOpen && !wasOpen && !hasAnnouncedOpenRef.current) {
      onCalculatorEvent?.('calculator_opened');
      hasAnnouncedOpenRef.current = true;
    }
    if (!isOpen && wasOpen) {
      onCalculatorEvent?.('calculator_closed');
      hasAnnouncedOpenRef.current = false;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, onCalculatorEvent]);

  const constrainToViewport = React.useCallback((x: number, y: number) => {
    const panel = panelRef.current;
    if (typeof window === 'undefined' || !panel) return { x, y };
    const padding = 8;
    const panelRect = panel.getBoundingClientRect();
    const maxX = Math.max(padding, window.innerWidth - panelRect.width - padding);
    const maxY = Math.max(padding, window.innerHeight - panelRect.height - padding);
    return {
      x: Math.min(Math.max(x, padding), maxX),
      y: Math.min(Math.max(y, padding), maxY),
    };
  }, []);

  const resetPosition = React.useCallback(() => {
    const panel = panelRef.current;
    if (typeof window === 'undefined' || !panel) return;
    const padding = 8;
    const panelRect = panel.getBoundingClientRect();
    const nextX = 24;
    const nextY = window.innerHeight - panelRect.height - 120;
    const clamped = {
      x: Math.min(Math.max(nextX, padding), Math.max(padding, window.innerWidth - panelRect.width - padding)),
      y: Math.min(Math.max(nextY, padding), Math.max(padding, window.innerHeight - panelRect.height - padding)),
    };
    setDesktopPosition(clamped);
    setPositionReady(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setPositionReady(false);
    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (typeof window === 'undefined' || !panel) return;
      const stored = sessionStorage.getItem(positionStorageKey);
      if (!stored) {
        resetPosition();
        return;
      }
      try {
        const parsed = JSON.parse(stored) as { x?: number; y?: number };
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          const clamped = constrainToViewport(parsed.x, parsed.y);
          setDesktopPosition(clamped);
          setPositionReady(true);
          return;
        }
      } catch {
        // Ignore malformed storage and fallback to reset position.
      }
      resetPosition();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, resetToken, positionStorageKey, constrainToViewport, resetPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      setDesktopPosition((prev) => constrainToViewport(prev.x, prev.y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, constrainToViewport]);

  useEffect(() => {
    if (!isOpen || !isDragging) return;
    const onMove = (event: PointerEvent) => {
      if (dragPointerIdRef.current !== event.pointerId) return;
      const targetX = event.clientX - dragOffsetRef.current.x;
      const targetY = event.clientY - dragOffsetRef.current.y;
      setDesktopPosition(constrainToViewport(targetX, targetY));
    };
    const onUp = (event: PointerEvent) => {
      if (dragPointerIdRef.current !== event.pointerId) return;
      dragPointerIdRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [constrainToViewport, isDragging, isOpen]);

  useEffect(() => {
    if (!isOpen || !positionReady) return;
    sessionStorage.setItem(positionStorageKey, JSON.stringify(desktopPosition));
  }, [desktopPosition, isOpen, positionReady, positionStorageKey]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const hasError = useMemo(() => display === 'Error', [display]);

  const announceUsed = () => {
    if (hasAnnouncedUsedRef.current) return;
    onCalculatorEvent?.('calculator_used');
    hasAnnouncedUsedRef.current = true;
  };

  const inputDigit = (digit: string) => {
    announceUsed();
    if (hasError) {
      setDisplay(digit);
      setWaitingForOperand(false);
      return;
    }
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
      return;
    }
    setDisplay((prev) => (prev === '0' ? digit : `${prev}${digit}`));
  };

  const inputDecimal = () => {
    announceUsed();
    if (hasError) {
      setDisplay('0.');
      setWaitingForOperand(false);
      return;
    }
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes('.')) setDisplay((prev) => `${prev}.`);
  };

  const applyOperator = (nextOperator: Exclude<Operator, null>) => {
    announceUsed();
    const currentValue = Number(display);
    if (!Number.isFinite(currentValue)) {
      setDisplay('Error');
      return;
    }
    if (storedValue === null) {
      setStoredValue(currentValue);
      setOperator(nextOperator);
      setWaitingForOperand(true);
      return;
    }
    if (operator) {
      const result = compute(storedValue, currentValue, operator);
      if (!Number.isFinite(result)) {
        setDisplay('Error');
        setStoredValue(null);
        setOperator(null);
        setWaitingForOperand(true);
        return;
      }
      setDisplay(formatNumber(result));
      setStoredValue(result);
      setOperator(nextOperator);
      setWaitingForOperand(true);
      return;
    }
    setOperator(nextOperator);
    setWaitingForOperand(true);
  };

  const handleEquals = () => {
    announceUsed();
    if (!operator || storedValue === null) return;
    const currentValue = Number(display);
    if (!Number.isFinite(currentValue)) {
      setDisplay('Error');
      return;
    }
    const result = compute(storedValue, currentValue, operator);
    if (!Number.isFinite(result)) {
      setDisplay('Error');
      setStoredValue(null);
      setOperator(null);
      setWaitingForOperand(true);
      return;
    }
    setDisplay(formatNumber(result));
    setStoredValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const handleBackspace = () => {
    announceUsed();
    if (hasError) {
      resetCalculator();
      return;
    }
    if (waitingForOperand) return;
    setDisplay((prev) => (prev.length <= 1 ? '0' : prev.slice(0, -1)));
  };

  if (!isOpen) return null;

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragPointerIdRef.current = event.pointerId;
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setIsDragging(true);
  };

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Calculator"
    >
      <div
        ref={panelRef}
        className={`pointer-events-auto w-full max-w-sm rounded-card border border-line bg-surface-card p-4 shadow-[0_24px_60px_-30px_rgba(2,6,23,0.85)] sm:p-5 ${positionReady ? '' : 'opacity-0'}`}
        style={{ left: desktopPosition.x, top: desktopPosition.y, position: 'fixed' }}
      >
        <div
          className={`mb-4 flex cursor-move items-center justify-between rounded-input border border-line bg-surface-page/50 px-3 py-2 ${isDragging ? 'cursor-grabbing' : ''}`}
          onPointerDown={startDrag}
          style={{ touchAction: 'none' }}
        >
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-300">Calculator</p>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-txt-secondary/70">Drag</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={resetPosition}
              className="rounded-md px-2 py-1 text-xs text-txt-secondary transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              Reset position
            </button>
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onClose}
              className="rounded-md px-2 py-1 text-xs text-txt-secondary transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              Close
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-card border border-line bg-surface-page/70 px-4 py-3 text-right">
          <p className="font-mono text-2xl text-txt-primary">{display}</p>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <button ref={firstButtonRef} type="button" onClick={resetCalculator} className={buttonClass}>
            C
          </button>
          <button type="button" onClick={handleBackspace} className={buttonClass}>
            ⌫
          </button>
          <button type="button" onClick={() => applyOperator('÷')} className={operatorClass}>
            ÷
          </button>
          <button type="button" onClick={() => applyOperator('×')} className={operatorClass}>
            ×
          </button>

          <button type="button" onClick={() => inputDigit('7')} className={buttonClass}>
            7
          </button>
          <button type="button" onClick={() => inputDigit('8')} className={buttonClass}>
            8
          </button>
          <button type="button" onClick={() => inputDigit('9')} className={buttonClass}>
            9
          </button>
          <button type="button" onClick={() => applyOperator('-')} className={operatorClass}>
            −
          </button>

          <button type="button" onClick={() => inputDigit('4')} className={buttonClass}>
            4
          </button>
          <button type="button" onClick={() => inputDigit('5')} className={buttonClass}>
            5
          </button>
          <button type="button" onClick={() => inputDigit('6')} className={buttonClass}>
            6
          </button>
          <button type="button" onClick={() => applyOperator('+')} className={operatorClass}>
            +
          </button>

          <button type="button" onClick={() => inputDigit('1')} className={buttonClass}>
            1
          </button>
          <button type="button" onClick={() => inputDigit('2')} className={buttonClass}>
            2
          </button>
          <button type="button" onClick={() => inputDigit('3')} className={buttonClass}>
            3
          </button>
          <button
            type="button"
            onClick={handleEquals}
            className="row-span-2 rounded-input border border-emerald-500/60 bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            =
          </button>

          <button type="button" onClick={() => inputDigit('0')} className={`${buttonClass} col-span-2`}>
            0
          </button>
          <button type="button" onClick={inputDecimal} className={buttonClass}>
            .
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimCalculator;
